/* One form-scoped validation path for Miravel product selectors. */
(() => {
  if (window.MiravelProductValidation) return;
  const rootSelector = '.mv-personalization, custom-product-field, [data-mv-validation-root]';
  const fieldSelector = 'input, select, textarea';
  const errorClass = 'mv-personalization-field-error';
  const invalidClass = 'mv-personalization-field-invalid';
  let errorId = 0;
  const language = (document.documentElement.lang || 'es').split('-')[0];
  const messages = { es: 'Campo obligatorio', en: 'Required field', fr: 'Champ obligatoire', it: 'Campo obbligatorio', de: 'Pflichtfeld' };
  const required = field => field.required || field.getAttribute('aria-required') === 'true' || field.closest('custom-product-field')?.dataset.required === 'true';
  const isProductForm = form => form?.tagName === 'FORM' && /\/cart\/add(?:[.?/]|$)/.test(form.getAttribute('action') || '');
  const formFor = element => {
    const explicit = element?.form || document.getElementById(element?.getAttribute?.('form') || '');
    if (isProductForm(explicit)) return explicit;
    const enclosing = element?.closest?.('form');
    if (isProductForm(enclosing)) return enclosing;
    const sticky = element?.closest?.('sticky-atc');
    if (sticky?.dataset.section) return document.getElementById(`product-form-${sticky.dataset.section}`);
    const area = element?.closest?.('.product__info-wrapper, [id^="MainProduct-"], quick-add-modal');
    return area?.querySelector('form[action*="/cart/add"]') || null;
  };
  const rootsFor = form => [...document.querySelectorAll(rootSelector)].filter(root => {
    const id = root.dataset.mvValidationForm || root.querySelector('[form]')?.getAttribute('form') ||
      (root.matches('custom-product-field') && root.dataset.section ? `product-form-${root.dataset.section}` : '');
    return id ? id === form.getAttribute('id') : formFor(root) === form;
  });
  const controlFor = field => {
    const container = field.closest('.mv-personalization__field, .product-form__input, custom-product-field, [data-mv-validation-field]') || field.parentElement;
    const anchor = field.closest('.mv-personalization__select-wrap, .select') || field;
    const focusTarget = container?.querySelector('.mv-stone-picker__button, .mv-select-picker__button, .mv-size-picker__button') || field;
    return { container, anchor, focusTarget };
  };
  const clearField = field => {
    const { container, focusTarget } = controlFor(field);
    if (!container) return;
    const error = container.querySelector(`.${errorClass}`);
    if (error) {
      for (const target of new Set([field, focusTarget])) {
        const refs = (target.getAttribute('aria-describedby') || '').split(/\s+/).filter(id => id && id !== error.id);
        if (refs.length) target.setAttribute('aria-describedby', refs.join(' '));
        else target.removeAttribute('aria-describedby');
      }
      error.remove();
    }
    container.classList.remove(invalidClass, 'mv-veria-field-invalid');
    container.querySelector('.mv-veria-field-error')?.remove();
    for (const target of new Set([field, focusTarget])) target.setAttribute('aria-invalid', 'false');
  };
  const markField = (field, message) => {
    const { container, anchor, focusTarget } = controlFor(field);
    if (!container || !anchor) return field;
    clearField(field);
    const placeholder = field.dataset.mvRequiredMessage || field.getAttribute('placeholder') ||
      (field.dataset.mvSizeUnselected === 'true' ? focusTarget.textContent.trim() : '') ||
      (field.tagName === 'SELECT' ? [...field.options].find(option => !option.value)?.textContent.trim() : '') ||
      field.getAttribute('aria-label') || messages[language] || messages.es;
    const error = document.createElement('span');
    error.id = `mv-required-${++errorId}`;
    error.className = errorClass;
    error.setAttribute('role', 'alert');
    error.textContent = `* ${(message || placeholder).replace(/^\*\s*/, '')}`;
    anchor.insertAdjacentElement('beforebegin', error);
    container.classList.add(invalidClass);
    for (const target of new Set([field, focusTarget])) {
      target.setAttribute('aria-invalid', 'true');
      target.setAttribute('aria-describedby', `${target.getAttribute('aria-describedby') || ''} ${error.id}`.trim());
    }
    return focusTarget;
  };
  const collectFields = form => {
    const fields = new Set([...form.elements].filter(field => field.matches?.(fieldSelector)));
    rootsFor(form).forEach(root => root.querySelectorAll(fieldSelector).forEach(field => {
      if (!field.form || field.form === form) fields.add(field);
    }));
    return [...fields].filter(field => {
      if (field.disabled || field.type === 'hidden' || !required(field)) return false;
      // Source selects stay required; inactive dynamic rows do not.
      const row = field.closest('.mv-personalization__field, .product-form__input, [data-mv-validation-field]');
      return !row?.closest('[hidden]');
    }).sort((a, b) => a.compareDocumentPosition(b) & 2 ? 1 : -1);
  };
  const isValid = field => {
    if (field.dataset.mvSizeUnselected === 'true') return false;
    if (field.type === 'checkbox' && required(field) && !field.checked) return false;
    if (field.type === 'radio' && required(field)) {
      const group = field.form ? [...field.form.elements] : [...field.parentElement.querySelectorAll('input[type="radio"]')];
      if (!group.some(other => other.type === 'radio' && other.name === field.name && other.checked)) return false;
    }
    if (field.type !== 'radio' && field.type !== 'checkbox' && !String(field.value || '').trim()) return false;
    return field.checkValidity();
  };
  const validate = (form, showErrors = true) => {
    if (!isProductForm(form)) return true;
    let first = null;
    collectFields(form).forEach(field => {
      if (isValid(field)) clearField(field);
      else {
        const target = showErrors ? markField(field) : field;
        if (!first) first = target;
      }
    });
    if (first && showErrors) {
      first.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      first.focus?.({ preventScroll: true });
    }
    return !first;
  };
  window.MiravelProductValidation = Object.freeze({ validate, collectFields, markField, clearField, formFor });
  const blockInvalid = (event, form) => {
    if (validate(form)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  };
  window.addEventListener('click', event => {
    const button = event.target.closest?.('button[name="add"], .main-product-atc, .sticky-atc__add_button, .product-form__submit');
    if (!button || button.closest('sticky-atc[data-scroll-btn="true"]')) return;
    blockInvalid(event, formFor(button));
  }, true);
  window.addEventListener('submit', event => blockInvalid(event, event.target), true);
  // Run after the widget has synchronized count and custom validity.
  const clearResolved = event => {
    const field = event.target;
    if (!field.matches?.(fieldSelector)) return;
    const form = formFor(field);
    if (!isProductForm(form)) return;
    if (field.disabled || !required(field) || isValid(field)) clearField(field);
    rootsFor(form).forEach(root => root.querySelectorAll(`.${invalidClass}`).forEach(row => {
      const input = row.querySelector(fieldSelector);
      if (input && (row.hidden || input.disabled || !required(input))) clearField(input);
    }));
  };
  document.addEventListener('input', clearResolved);
  document.addEventListener('change', clearResolved);
})();
